import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Tooltip } from 'antd'
import { get } from 'lodash'
import {
  IconPDLSparkle,
  IconCheck,
  IconClose,
  IconEditMetaData,
} from '@edulastic/icons'
import {
  getBulkItemStatusSelector,
  getBulkSuggestionsSelector,
  saveItemStandardAction,
  clearBulkItemAction,
  BULK_ITEM_STATUS,
} from '../ducks/bulkQuestionMetadata'
import { DIFFICULTY_OPTIONS, DOK_OPTIONS } from '../constants/form'
import {
  StyledItemStandardsRow,
  StyledItemStandardsContent,
  StyledItemStandardsDetails,
  StyledItemStandardsTextWrap,
  StyledStandardText,
  StyledStandardMeta,
  StyledLoadingDot,
  StyledRowActions,
  StyledSaveAction,
  StyledEditAction,
  StyledDiscardAction,
} from './styled'

const getOptionLabel = (options, value) =>
  options.find((option) => option.value === value)?.label || value

const LoadingRow = ({ label, dataCy }) => (
  <StyledItemStandardsRow data-cy={dataCy} aria-live="polite" aria-busy="true">
    <StyledItemStandardsContent>
      <IconPDLSparkle width={16} height={16} aria-hidden="true" />
      <span>
        {label}
        <StyledLoadingDot>.</StyledLoadingDot>
        <StyledLoadingDot delay="0.2s">.</StyledLoadingDot>
        <StyledLoadingDot delay="0.4s">.</StyledLoadingDot>
      </span>
    </StyledItemStandardsContent>
  </StyledItemStandardsRow>
)

const ItemStandardsStatus = ({
  item,
  status,
  suggestions,
  saveItem,
  clearItem,
  onEdit,
}) => {
  const itemId = item?._id

  const dokLabel = useMemo(() => {
    const questions = get(item, 'data.questions', [])
    const primarySuggestion = questions
      .map((q) => suggestions?.[q?.id])
      .find((s) => s?.standard)
    return primarySuggestion?.dok
      ? getOptionLabel(DOK_OPTIONS, primarySuggestion.dok)
      : null
  }, [item, suggestions])

  const difficultyLabel = useMemo(() => {
    const questions = get(item, 'data.questions', [])
    const primarySuggestion = questions
      .map((q) => suggestions?.[q?.id])
      .find((s) => s?.standard)
    return primarySuggestion?.difficulty
      ? getOptionLabel(DIFFICULTY_OPTIONS, primarySuggestion.difficulty)
      : null
  }, [item, suggestions])

  if (!status) return null

  if (status === BULK_ITEM_STATUS.GENERATING) {
    return (
      <LoadingRow
        dataCy="itemStandardsGenerating"
        label="Generating Standards, Difficulty, DOK"
      />
    )
  }

  if (status === BULK_ITEM_STATUS.SAVING) {
    return <LoadingRow dataCy="itemStandardsSaving" label="Saving Standard" />
  }

  if (status === BULK_ITEM_STATUS.ERROR) {
    return (
      <StyledItemStandardsRow className="error" data-cy="itemStandardsError">
        <StyledItemStandardsContent>
          <IconPDLSparkle width={16} height={16} aria-hidden="true" />
          Couldn&apos;t generate standards for this item.
        </StyledItemStandardsContent>
      </StyledItemStandardsRow>
    )
  }

  const questions = get(item, 'data.questions', [])
  const questionIds = questions.map((q) => q?.id).filter(Boolean)
  const primarySuggestion = questions
    .map((q) => suggestions?.[q?.id])
    .find((s) => s?.standard)
  const primaryStandard = primarySuggestion?.standard

  if (!primaryStandard) {
    return (
      <StyledItemStandardsRow data-cy="itemStandardsNoSuggestion">
        <StyledItemStandardsContent>
          <IconPDLSparkle width={16} height={16} aria-hidden="true" />
          No standard suggestion found.
        </StyledItemStandardsContent>
        <StyledRowActions role="group" aria-label="Suggestion actions">
          <StyledDiscardAction
            type="button"
            data-cy="itemStandardsDiscard"
            aria-label="Reject suggestion"
            onClick={() => clearItem({ itemId, questionIds })}
          >
            <IconClose width={10} height={10} color="#151d34" aria-hidden />
          </StyledDiscardAction>
        </StyledRowActions>
      </StyledItemStandardsRow>
    )
  }

  const { identifier, description } = primaryStandard
  const standardText = `${identifier}${description ? ` (${description})` : ''}`

  return (
    <StyledItemStandardsRow
      data-cy="itemStandardsSuggestion"
      aria-label={`Suggested standard ${standardText}${
        difficultyLabel ? `, difficulty ${difficultyLabel}` : ''
      }${dokLabel ? `, DOK ${dokLabel}` : ''}`}
    >
      <StyledItemStandardsContent>
        <IconPDLSparkle width={16} height={16} aria-hidden="true" />
        <StyledItemStandardsDetails>
          <StyledItemStandardsTextWrap>
            <Tooltip title={standardText}>
              <StyledStandardText>{standardText}</StyledStandardText>
            </Tooltip>
          </StyledItemStandardsTextWrap>
          {difficultyLabel && (
            <StyledStandardMeta data-cy="itemStandardsDifficulty">
              {difficultyLabel}
            </StyledStandardMeta>
          )}
          {dokLabel && (
            <StyledStandardMeta data-cy="itemStandardsDok">
              {dokLabel}
            </StyledStandardMeta>
          )}
        </StyledItemStandardsDetails>
      </StyledItemStandardsContent>
      <StyledRowActions role="group" aria-label="Suggestion actions">
        <StyledEditAction
          type="button"
          data-cy="itemStandardsEdit"
          aria-label="Edit suggestion"
          onClick={() => onEdit(itemId)}
        >
          <IconEditMetaData width={16} height={16} fill="#158463" aria-hidden />
          Edit
        </StyledEditAction>
        <StyledSaveAction
          type="button"
          data-cy="itemStandardsSave"
          aria-label="Save suggestion"
          onClick={() => saveItem({ itemId, questionIds })}
        >
          <IconCheck width={12} height={12} color="#158463" aria-hidden />
          Save
        </StyledSaveAction>
        <StyledDiscardAction
          type="button"
          data-cy="itemStandardsDiscard"
          aria-label="Reject suggestion"
          onClick={() => clearItem({ itemId, questionIds })}
        >
          <IconClose width={10} height={10} color="#151d34" aria-hidden />
        </StyledDiscardAction>
      </StyledRowActions>
    </StyledItemStandardsRow>
  )
}

ItemStandardsStatus.propTypes = {
  item: PropTypes.object.isRequired,
  status: PropTypes.string,
  suggestions: PropTypes.object,
  saveItem: PropTypes.func.isRequired,
  clearItem: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
}

ItemStandardsStatus.defaultProps = {
  status: null,
  suggestions: {},
  onEdit: () => {},
}

export default connect(
  (state, { item }) => ({
    status: getBulkItemStatusSelector(state)?.[item?._id],
    suggestions: getBulkSuggestionsSelector(state),
  }),
  {
    saveItem: saveItemStandardAction,
    clearItem: clearBulkItemAction,
  }
)(ItemStandardsStatus)
